// !!! IMPORTANT !!!
//
// This file affects the global flows redux state, used in lots of places.
// You may need to make the same changes to both the beta-editor and
// classic-editor code. Edit with caution and test changes thoroughly!

import _ from 'lodash';
import { $set, update } from 'qim';
import { routeBackTo, routeTo as routeToAction } from 'redux-router-kit';

import { utils as editorUtils } from '@zapier/editor';

import * as Account from 'app/entities/Account';
import * as Member from 'app/entities/Member';
import * as Profile from 'app/entities/Profile';
import AccountActions from 'app/account/AccountActions';
import ActionTypes from 'app/editor/ActionTypes';
import AuthActions from 'app/auth/AuthActions';
import ServiceUtils from 'app/service/ServiceUtils';
import SharedZapActions from 'app/shared-zap/SharedZapActions';
import TrackActions from 'app/track/TrackActions';
import pages from 'app/beta-editor/Pages';
import Data, { isTemplateSuitableForCapabilities } from 'app/beta-editor/Data';
import NodeUtils, {
  getCorrectNodeTypeOf,
  getDefaultAction,
  getRetainedNodeMeta,
  isFilterAPI,
} from 'app/beta-editor/utils/NodeUtils';
import { NORMAL_EDIT_MODE } from 'app/beta-editor/constants/EditModes';
import { getBooleanQueryParam } from 'app/common/utils/RouterSelectors';
import { hasLoadedAuths } from 'app/auth/Selectors';
import { haveAccountsAndProfilesSuccessfullyLoaded } from 'app/account/Selectors';
import { isZapInTrash } from 'app/dashboard/utils/DashboardUtils';
import { nodeTemplatesFromQuery } from 'app/beta-editor/utils/TemplateUtils';
import { trackException } from 'app/error/tracking';
import { willPromisify } from 'app/common/middlewares/willPromiseMiddleware';
import {
  PATHS_GROUP_ATTRS,
  isLastPathInGroup,
  isPathApi,
  isPathNode,
  isPathsGroupNode,
  makePathNodeAttrsGenerator,
} from 'app/beta-editor/utils/PathUtils';
import { PATH_SETTINGS_PAGE, ZT_API_PARAM } from 'app/beta-editor/constants';
import { cleanObjectOfEmptyValues, get } from 'app/common/CommonUtils';
import {
  createEditorUrl,
  getContinueFromStepActions,
  isTriggerStep,
  setEditMode,
} from 'app/beta-editor/utils/FlowUtils';
import { getParentId, getRootNode } from 'app/beta-editor/utils/NodeTrees';
import {
  isCsrfErrorResponse,
  isUnauthorizedErrorResponse,
} from 'app/common/fetch';
import {
  selectCurrentPublishForm,
  selectFlow,
  selectIsFlowTemplate,
  selectNode,
  selectPublicZapRecipeId,
} from 'app/beta-editor/Selectors';

const actions = {
  async getCapabilities({ currentAccountId, sharedZapId, query }) {
    let shouldUseSimpleZapSetup = false;
    let isFromNouns;
    let nounMeta;
    let ztId;
    // Only run nodeTemplatesFromQuery if sharedZapId is not set #29312
    let nodeTemplates = sharedZapId ? null : nodeTemplatesFromQuery(query);

    // ZT Path - we only want to call capabilities once depending on whether a query or sharedZapId was passed in
    if (sharedZapId) {
      const capabilities = await Data.getCapabilitiesBySharedZapId(
        currentAccountId,
        sharedZapId
      );

      isFromNouns = editorUtils.formatDataFromCapabilities(capabilities)
        .isFromNouns;
      nounMeta = editorUtils.formatDataFromCapabilities(capabilities).nounMeta;
      ztId = editorUtils.formatDataFromCapabilities(capabilities).ztId;
      shouldUseSimpleZapSetup = editorUtils.formatDataFromCapabilities(
        capabilities
      ).isSimpleZapCapable;

      if (isFromNouns) {
        const sharedZap = await Data.createNewZapJson({ sharedZapId });
        const { title } = sharedZap;
        nodeTemplates = editorUtils.formatDataFromCapabilities(capabilities)
          .templates;
        nodeTemplates[0].title = title;
      }

      return {
        shouldUseSimpleZapSetup,
        isFromNouns,
        nounMeta,
        nodeTemplates,
        // Need to do this for proper mapping via the fetched ZT in order to turn on Simple Zap
        ztId: ztId ? ztId : sharedZapId,
      };
    }

    // Query / outline path - we only want to call capabilities once depending on whether a query or sharedZapId was passed in
    const isSuitableForCapabilities = isTemplateSuitableForCapabilities(
      nodeTemplates
    );

    // Don't attempt to use nouns or send event data if either not all required fields
    // were specified by the user, or if there's not exactly two elements in the template array
    if (isSuitableForCapabilities) {
      const templatesForCapabilities = nodeTemplates.map(template =>
        _.pick(template, ['selected_api', 'type_of', 'action'])
      );

      const capabilities = await Data.getCapabilities(
        currentAccountId,
        templatesForCapabilities
      );

      isFromNouns = editorUtils.formatDataFromCapabilities(capabilities)
        .isFromNouns;
      nounMeta = editorUtils.formatDataFromCapabilities(capabilities).nounMeta;
      ztId = editorUtils.formatDataFromCapabilities(capabilities).ztId;
      shouldUseSimpleZapSetup = editorUtils.formatDataFromCapabilities(
        capabilities
      ).isSimpleZapCapable;
    }

    return {
      shouldUseSimpleZapSetup,
      isFromNouns,
      nounMeta,
      nodeTemplates,
      // Need to do this for proper mapping via the fetched ZT in order to turn on Simple Zap
      ztId: ztId ? ztId : sharedZapId,
    };
  },

  createZap({
    folderId,
    sharedZapId,
    shouldReplaceRoute,
    query,
    referrer,
    isTemplate,
  }) {
    return async (dispatch, getState) => {
      const state = getState();
      const currentAccount = Account.selectors.all.currentAccount(state);
      const currentAccountId = Account.selectors.id(currentAccount);
      let shouldUseInstantZap = false;

      const {
        shouldUseSimpleZapSetup,
        isFromNouns,
        nounMeta,
        nodeTemplates,
        ztId,
      } = await this.getCapabilities({
        currentAccountId,
        sharedZapId,
        query,
      });

      // We need to use the ZT from capabilities if it is supported
      sharedZapId = ztId ? ztId : sharedZapId;

      return dispatch({
        type: ActionTypes.CREATE_ZAP,
        shouldLoginReplaceRoute: true,
        shouldPreferSignUp: !!sharedZapId,
        shouldRequireLogin: error => {
          if (!error) {
            return false;
          }
          if (
            isUnauthorizedErrorResponse(error) ||
            // Need to check `error.json` as well because if `error`
            // isn't a `Response` object then it won't have any headers
            // for `isCsrfErrorResponse` to check.
            (isCsrfErrorResponse(error) && !error.json)
          ) {
            return true;
          }
          return false;
        },
        promise: Promise.resolve({})
          .then(() => Data.createNewZapJson({ sharedZapId }))
          .then(sharedZap => {
            const instantZap =
              sharedZap && editorUtils.getInstantHardcodedTemplate(sharedZap);
            shouldUseInstantZap = Boolean(instantZap);

            return Data.createZap(currentAccountId, {
              sharedZap,
              nodeTemplates:
                shouldUseInstantZap || (sharedZapId && !isFromNouns)
                  ? undefined
                  : nodeTemplates,
              // Put Zap in home folder if it's created in the trash or held folder.
              folderId: !Number.isInteger(folderId) ? undefined : folderId,
              query,
              referrer,
              isTemplate,
              isFromNouns,
            });
          })
          .then(({ flowId, nodes, isBlank }) => {
            const editor = shouldUseInstantZap
              ? 'instant'
              : shouldUseSimpleZapSetup
              ? 'szs'
              : 'beta';

            dispatch(
              TrackActions.trackUseCaseFound({
                nodeId: flowId,
                nodeIds: sharedZapId ? null : nodeTemplatesFromQuery(query),
                referrer,
                editor,
                zapTemplate: sharedZapId,
                simpleZapSetup: !shouldUseInstantZap || shouldUseSimpleZapSetup,
                nounMeta,
              })
            );

            dispatch(
              this.loadNewFlow(flowId, nodes, {
                isBlank,
                isTemplate,
                shouldUseSimpleZapSetup,
              })
            );

            if (shouldUseInstantZap) {
              dispatch(
                routeToAction(
                  createEditorUrl({
                    isInstantZap: true,
                    flowId,
                  }),
                  {
                    replace: shouldReplaceRoute,
                  }
                )
              );

              return;
            }

            const simpleZapUrl = createEditorUrl({
              flowId,
              isTemplate,
              query: { is_new: true, simple: true },
            });

            const regularZapUrl = createEditorUrl({
              flowId,
              isTemplate,
              query: { is_new: true },
            });

            const editorUrl = shouldUseSimpleZapSetup
              ? simpleZapUrl
              : regularZapUrl;

            dispatch(
              routeToAction(editorUrl, {
                replace: shouldReplaceRoute,
              })
            );
          }),
      });
    };
  },

  loadZaps() {
    return willPromisify(Data.zaps, [Account.selectors.all.currentAccountId], {
      type: ActionTypes.LOAD_ZAPS,
      resultKey: 'zaps',
    });
  },
  loadBlankFlow(flowId, nodesArray, { isTemplate } = {}) {
    return async dispatch => {
      dispatch({
        type: ActionTypes.LOAD_FLOW,
        flowId,
        isNew: true,
      });

      try {
        const flow = await Data.newFlow(flowId, nodesArray, { isTemplate });

        if (flow.account_id) {
          dispatch(
            Account.actions.setAndStoreCurrentAccountId(flow.account_id)
          );
        }

        dispatch({
          type: ActionTypes.LOAD_FLOW_DONE,
          flow,
        });

        dispatch(
          TrackActions.trackUseCaseFound({
            editor: 'beta',
            nodeId: flowId,
          })
        );

        // For team accounts loading the auth page for the first time can
        // be very slow as we load their team member accounts, which is slow.
        // Go ahead and load them in the background here, so that by the time
        // they get to an auth page the members are already loaded.
        // The loadCollectionIfTeamAccount method will only load if the members
        // are not already loaded.
        dispatch(Member.actions.loadCollectionIfTeamAccount());
      } catch (error) {
        dispatch({
          type: ActionTypes.LOAD_FLOW_FAIL,
          error,
          flowId,
        });
      }
    };
  },

  loadNewFlow(
    flowId,
    nodesArray,
    { isBlank, isTemplate, shouldUseSimpleZapSetup } = {}
  ) {
    if (isBlank) {
      return this.loadBlankFlow(flowId, nodesArray, { isTemplate });
    }
    return async (dispatch, getState) => {
      dispatch({
        type: ActionTypes.LOAD_FLOW,
        flowId,
        isNew: true,
      });

      try {
        const flow = await Data.newFlow(flowId, nodesArray, { isTemplate });
        const rootNode = getRootNode(flow.nodes);

        if (flow.account_id) {
          dispatch(
            Account.actions.setAndStoreCurrentAccountId(flow.account_id)
          );
        }

        const hasAuths = hasLoadedAuths(getState());
        if (!hasAuths) {
          await dispatch(AuthActions.loadAuths());
        }

        // If this zap was created from a shared zap, then we want to load the shared zap too.
        if (rootNode.guided_recipe_origin_id) {
          // If this zap was created from a shared zap, then we want to load the shared zap too.
          dispatch(
            SharedZapActions.loadSharedZap(rootNode.guided_recipe_origin_id, {
              shouldSuppress404Errors: true,
            })
          );
        }
        dispatch({
          type: ActionTypes.LOAD_FLOW_DONE,
          flow,
        });

        const nodeIdsForNeeds = _.map(flow.nodes)
          .filter(node => node.selected_api && node.type_of)
          .map(node => node.id);

        dispatch(
          actions.loadAllNeedsForZap(flowId, nodeIdsForNeeds, { isTemplate })
        );

        dispatch(
          TrackActions.trackUseCaseFound({
            editor: shouldUseSimpleZapSetup ? 'szs' : 'beta',
            nodeId: flowId,
          })
        );

        // For team accounts loading the auth page for the first time can
        // be very slow as we load their team member accounts, which is slow.
        // Go ahead and load them in the background here, so that by the time
        // they get to an auth page the members are already loaded.
        // The loadCollectionIfTeamAccount method will only load if the members
        // are not already loaded.
        return dispatch(Member.actions.loadCollectionIfTeamAccount());
      } catch (error) {
        return dispatch({
          type: ActionTypes.LOAD_FLOW_FAIL,
          error,
          flowId,
        });
      }
    };
  },

  loadFlow(
    flowId,
    nodeId,
    pageKey,
    { isNew = false, isTemplate, shouldUseSimpleZapSetup } = {}
  ) {
    return (dispatch, getState) => {
      // For team accounts loading the auth page for the first time can
      // be very slow as we load their team member accounts, which is slow.
      // Go ahead and load them in the background here, so that by the time
      // they get to an auth page the members are already loaded.
      // The loadCollectionIfTeamAccount method will only load if the members
      // are not already loaded.
      dispatch(Member.actions.loadCollectionIfTeamAccount());

      dispatch({
        type: ActionTypes.LOAD_FLOW,
        flowId,
        isNew,
      });

      const originalAccountId = Account.selectors.all.currentAccountId(
        getState()
      );

      return Data.flow(flowId, { isTemplate })
        .then(flow => {
          if (flow.account_id) {
            dispatch(
              Account.actions.setAndStoreCurrentAccountId(flow.account_id)
            );
          }
          return flow;
        })
        .then(flow => {
          // If the flow.account_id is different that the current account,
          // we will have switched to the flow account id above. In that case,
          // we need to make sure to load the auths and account *after*
          // switching accounts. Otherwise we won't have the right auths,
          // and the user will see 'restricted account' and other weirdness
          // on their auth steps.
          const hasAuths = hasLoadedAuths(getState());
          const hasChangedAccount = originalAccountId !== flow.account_id;

          if (
            hasChangedAccount ||
            !haveAccountsAndProfilesSuccessfullyLoaded(getState())
          ) {
            dispatch(AccountActions.loadAccount());
          }
          if (hasChangedAccount || !hasAuths) {
            // We need to wait until *after* the auths have loaded before continuing,
            // in the case where the account was switched, to prevent weird transient
            // 'restricted account' weirdness from being displayed as the editor tries
            // to display auth info based on auths for the wrong account
            return dispatch(AuthActions.loadAuths()).then(() => flow);
          }
          return flow;
        })
        .then(flow => {
          const rootNode = getRootNode(flow.nodes);
          // If this zap was created from a shared zap, then we want to load the
          // shared zap too.
          if (rootNode.guided_recipe_origin_id) {
            return dispatch(
              SharedZapActions.loadSharedZap(rootNode.guided_recipe_origin_id, {
                shouldSuppress404Errors: true,
              })
            ).then(
              () => flow,
              error => {
                // If the shared zap 404's, we want to gracefully handle it and continue.
                if (error.status === 404) {
                  return setEditMode(flow, NORMAL_EDIT_MODE);
                }

                throw error;
              }
            );
          }
          return flow;
        })
        .then(flow => {
          const pathsGroupParent = _.find(flow.nodes, isPathsGroupNode);
          const hasNoPathNodes = !_.some(flow.nodes, isPathNode);
          return pathsGroupParent &&
            isPathsGroupNode(pathsGroupParent) &&
            hasNoPathNodes
            ? dispatch(
                actions.createInitialPathsForGroup(flowId, pathsGroupParent.id)
              ).then(() => flow)
            : flow;
        })
        .then(flow => {
          return dispatch({
            type: ActionTypes.LOAD_FLOW_DONE,
            flow,
          });
        })
        .then(flowDoneResult => {
          const { flow } = flowDoneResult;
          const nodeIdsForNeeds = _.map(flow.nodes)
            .filter(node => {
              return node.selected_api && node.type_of;
            })
            .map(node => node.id);
          dispatch(
            actions.loadAllNeedsForZap(flowId, nodeIdsForNeeds, { isTemplate })
          );

          return flowDoneResult;
        })
        .then(
          ({ flow: { id: fId } }) => {
            const flow = selectFlow(fId, getState());
            dispatch(
              TrackActions.trackUseCaseFound({
                editor: shouldUseSimpleZapSetup ? 'szs' : 'beta',
                nodeId,
              })
            );

            const shouldOpenTrigger = getBooleanQueryParam(
              getState(),
              'auto-create'
            );

            if (nodeId && pageKey) {
              dispatch(actions.loadPage(flowId, nodeId, pageKey));
              if (pageKey === 'sample') {
                return dispatch(
                  actions.runNode(flowId, nodeId, flow.taskState)
                );
              }
              if (pageKey === 'fields') {
                if (flow.nodes[nodeId].type_of === 'filter') {
                  return dispatch(
                    actions.loadGives(flow, nodeId, flow.taskState)
                  );
                }
              }
            } else if (shouldOpenTrigger) {
              return dispatch(
                routeToAction(
                  createEditorUrl({ flowId, nodeId, pageKey: 'setup' })
                )
              );
            }
            return undefined;
          },
          error => {
            dispatch({
              type: ActionTypes.LOAD_FLOW_FAIL,
              error,
              flowId,
            });
          }
        );
    };
  },

  loadFlowProgress(flowId, progress) {
    return {
      type: ActionTypes.LOAD_FLOW_PROGRESS,
      flowId,
      progress,
    };
  },

  loadPage(flowId, nodeId, pageKey) {
    return {
      type: ActionTypes.LOAD_FLOW_PAGE,
      flowId,
      nodeId,
      pageKey,
      promise: Data.flowPage(flowId, nodeId, pageKey),
      resultKey: 'page',
    };
  },

  updateTitle(flowId, title) {
    return {
      type: ActionTypes.UPDATE_FLOW_TITLE,
      flowId,
      title,
    };
  },

  saveUpdateTitle(flowId, title) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId });
      return dispatch({
        type: ActionTypes.UPDATE_FLOW_TITLE,
        flowId,
        title,
        promise: Data.patchZap(flowId, { title }, { isTemplate }),
      });
    };
  },

  saveUpdateCustomUser(flowId, customuserId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId });
      return dispatch({
        type: ActionTypes.UPDATE_FLOW_OWNER,
        flowId,
        customuserId,
        promise: Data.transferZap(flowId, { customuserId }, { isTemplate }),
      });
    };
  },

  createPath(flowId, pathsGroupNodeId, pathNodeAttrs) {
    return (dispatch, getState) => {
      const accountId = Account.selectors.all.currentAccountId(getState());
      return dispatch({
        type: ActionTypes.CREATE_PATH,
        flowId,
        pathsGroupNodeId,
        pathNodeAttrs,
        promise: Data.createPathAndCheckPaused(
          accountId,
          flowId,
          pathsGroupNodeId,
          pathNodeAttrs
        ),
      });
    };
  },

  createInitialPathsForGroup(flowId, pathsGroupNodeId) {
    return (dispatch, getState) => {
      const state = getState();
      const flow = selectFlow(flowId, state);
      const routeTo = createEditorUrl({
        flowId,
        nodeId: pathsGroupNodeId,
        pageKey: PATH_SETTINGS_PAGE,
      });
      const accountId = Account.selectors.all.currentAccountId(getState());

      // Create two paths
      const genPathAttrs = makePathNodeAttrsGenerator(flow);
      const firstPathAttrs = genPathAttrs();
      const secondPathAttrs = genPathAttrs();
      dispatch(actions.addStep(flowId));
      return dispatch({
        type: ActionTypes.CREATE_INITIAL_PATHS_FOR_GROUP,
        accountId,
        flowId,
        pathsGroupNodeId,
        pathNodeAttrsList: [firstPathAttrs, secondPathAttrs],
        routeTo,
        promise: Data.createInitialPathsForGroupAndCheckPaused(
          accountId,
          flowId,
          pathsGroupNodeId,
          [firstPathAttrs, secondPathAttrs]
        ),
      }).then(result => {
        // Update root node validation
        const rootNode = _.get(result, ['result', 'rootNode'], {});
        const planLimits = _.get(rootNode, ['plan_limits'], null);
        dispatch(actions.updateNodeValidation(flowId, rootNode.id, planLimits));

        dispatch(actions.addStepDone(flowId));
        return result;
      });
    };
  },

  addNode(
    flowId,
    parentNodeId,
    {
      type_of,
      before_id,
      selected_api,
      action,
      params = {},
      meta,
      updateField,
      authentication_id,
      routeToSubStep = 'setup',
      shouldRouteTo = true,
    } = {}
  ) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId });

      dispatch(actions.addStep(flowId));
      dispatch({
        type: ActionTypes.ADD_NODE,
        flowId,
      });

      return Data.createNode(
        Account.selectors.all.currentAccountId(getState()),
        {
          type_of,
          root_id: flowId,
          parent_id: parentNodeId,
          before_id,
          selected_api,
          action,
          params,
          meta,
          authentication_id,
          isTemplate,
        }
      )
        .then(node => Promise.all([node, Data.node(flowId, { isTemplate })]))
        .then(
          ([node, rootNode]) => {
            const routeTo = shouldRouteTo
              ? createEditorUrl({
                  flowId,
                  nodeId: node.id,
                  pageKey: routeToSubStep,
                  isTemplate: selectIsFlowTemplate(getState(), {
                    flowId,
                    nodeId: parentNodeId,
                  }),
                })
              : undefined;

            // Update root node plan limits
            dispatch(
              actions.updateNodeValidation(
                flowId,
                rootNode.id,
                rootNode.plan_limits
              )
            );

            dispatch([
              {
                type: ActionTypes.ADD_NODE_DONE,
                flowId,
                node,
                paused: rootNode.paused,
              },
              actions.addStepDone(flowId),
              routeToAction(routeTo),
            ]);

            if (updateField) {
              const updatedParams = NodeUtils.updateNodeParams(
                node,
                updateField
              );
              const updatedMeta = NodeUtils.updateSearchfillsMeta(
                node,
                updateField
              );

              const newNodeMeta = NodeUtils.newNodeMeta(node, updateField);

              Promise
                // Update the node params and meta
                .all([
                  dispatch(
                    actions.saveUpdateNodeParams(
                      flowId,
                      updateField.nodeId,
                      updatedParams,
                      {
                        fieldKey: updateField.fieldKey,
                        meta: updatedMeta,
                      }
                    )
                  ),
                  dispatch(
                    actions.saveUpdateNodeParams(
                      flowId,
                      node.id,
                      {},
                      { meta: newNodeMeta }
                    )
                  ),
                ])
                // Scroll to the newly added node
                .then(() => {
                  dispatch(actions.scrollToNode(flowId, node.id));
                });
            }

            return (isPathsGroupNode(node)
              ? dispatch(actions.createInitialPathsForGroup(flowId, node.id))
              : Promise.resolve()
            ).then(() => node);
          },
          error => {
            dispatch({
              type: ActionTypes.ADD_NODE_FAIL,
              error,
              flowId,
            });
          }
        );
    };
  },

  scrollToNode(flowId, nodeId) {
    return dispatch => {
      return Promise.resolve()
        .then(() => {
          dispatch({
            type: ActionTypes.SCROLL_TO_NODE,
            flowId,
            nodeId,
          });
          return Promise.resolve();
        })
        .then(() => {
          dispatch({
            type: ActionTypes.SCROLL_TO_NODE_RESET,
            flowId,
          });
        });
    };
  },

  addStep(flowId) {
    return { type: ActionTypes.ADD_STEP, flowId };
  },

  addStepDone(flowId) {
    return { type: ActionTypes.ADD_STEP_DONE, flowId };
  },

  updateNodeValidation(flowId, nodeId, validation) {
    return {
      type: ActionTypes.UPDATE_NODE_VALIDATION,
      flowId,
      nodeId,
      validation,
    };
  },

  loadDynamicDropdown(
    flowId,
    nodeId,
    taskState,
    fieldKey,
    field,
    params,
    page = 0
  ) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      return dispatch({
        type: ActionTypes.LOAD_NODE_DYNAMIC_DROPDOWN,
        flowId,
        nodeId,
        fieldKey,
        prefill: field.prefill,
        params,
        page,
        promise: Data.prefillChoices(nodeId, taskState, field, params, page, {
          isTemplate,
        }),
        resultKey: 'prefillChoices',
      });
    };
  },

  loadGives(flow, nodeId, taskState, onDangerouslyReceiveSuccess) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), {
        flowId: flow.id,
        nodeId,
      });

      return dispatch({
        type: ActionTypes.LOAD_NODE_GIVES,
        flow,
        nodeId,
        taskState,
        promise: Data.givesAll(flow, nodeId, { isTemplate }),
        resultKey: 'gives',
        onDangerouslyReceiveSuccess,
      });
    };
  },

  loadNeeds(flowId, nodeId, onDangerouslyReceiveSuccess) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      const needsParams = isTemplate && ZT_API_PARAM;
      return dispatch({
        type: ActionTypes.LOAD_NODE_NEEDS,
        flowId,
        nodeId,
        promise: Data.needs(nodeId, needsParams),
        resultKey: 'needs',
        onDangerouslyReceiveSuccess,
      });
    };
  },

  loadNeedsWithForcedCacheRebuild(flowId, nodeId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      const templateParams = isTemplate && ZT_API_PARAM;
      return dispatch({
        type: ActionTypes.LOAD_NODE_NEEDS,
        flowId,
        nodeId,
        promise: Data.needs(nodeId, { rebuild_cache: true, ...templateParams }),
        resultKey: 'needs',
      });
    };
  },

  loadAllNeedsForZap(flowId, nodeIds, options) {
    return {
      type: ActionTypes.LOAD_ALL_NEEDS_FOR_ZAP,
      flowId,
      promise: Data.nodesNeeds(nodeIds, options),
      resultKey: 'allNeeds',
    };
  },

  loadInvalidFieldMappings(flowId, nodeId, nodeSample) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      return dispatch({
        type: ActionTypes.LOAD_NODE_INVALID_FIELD_MAPPINGS,
        flowId,
        nodeId,
        nodeSample,
        promise: Data.invalidFieldMappings(nodeId, nodeSample, { isTemplate }),
        resultKey: 'invalidMappings',
      });
    };
  },

  updateNodeMeta(flowId, nodeId, meta, options) {
    const shouldPersist =
      options && options.hasOwnProperty('shouldPersist')
        ? options.shouldPersist
        : true;

    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      return dispatch({
        type: ActionTypes.UPDATE_NODE_META,
        flowId,
        nodeId,
        meta,
        promise: shouldPersist
          ? Data.patchNode(nodeId, { meta }, { isTemplate })
          : undefined,
      });
    };
  },

  updateWebHookTriggerMode(node, isSilent) {
    return actions.updateNodeMeta(
      node.root_id || node.id,
      node.id,
      update(['webhook', 'is_silent', $set(isSilent)], node.meta)
    );
  },

  loadNode(flowId, nodeId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      dispatch({
        type: ActionTypes.UPDATE_NODE,
        flowId,
        nodeId,
        attrs: {},
        promise: Data.node(nodeId, { isTemplate }),
        resultKey: 'node',
        options: {
          setSampleDirty: false,
        },
      });
    };
  },

  saveUpdateNode(
    flowId,
    nodeId,
    attrs,
    { isDirtyNeeds, onDangerouslyReceiveSuccess, shouldUseSimpleZapSetup }
  ) {
    return (dispatch, getState) => {
      const state = getState();
      const isTemplate = selectIsFlowTemplate(state, { flowId, nodeId });
      const flow = selectFlow(flowId, state);

      const thisNode = flow.nodes[nodeId];
      // We want to send a use-case-found event when user
      // selects a trigger the first time
      if (isTriggerStep(thisNode) && !thisNode.selected_api) {
        dispatch(
          TrackActions.trackUseCaseFound({
            editor: shouldUseSimpleZapSetup ? 'szs' : 'beta',
            referrer: 'beta-editor',
            nodeId,
          })
        );
      }

      return dispatch({
        type: ActionTypes.UPDATE_NODE,
        flowId,
        nodeId,
        attrs,
        promise: Data.patchNode(nodeId, attrs, {
          isTemplate,
        }).then(node => {
          if (isDirtyNeeds) {
            dispatch(actions.loadNeeds(flowId, nodeId));
          }
          if (isPathsGroupNode(node)) {
            return dispatch(
              actions.createInitialPathsForGroup(flowId, nodeId)
            ).then(() => node);
          }
          return node;
        }),
        resultKey: 'node',
        onDangerouslyReceiveSuccess,
      });
    };
  },

  saveUpdateNodeParams(
    flowId,
    nodeId,
    params,
    { fieldKey, meta, isDirtyNeeds = false } = {},
    cb
  ) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      const cleanedParams = cleanObjectOfEmptyValues(params);

      // TODO delete this once we resolve #32188
      const { filter_criteria } = cleanedParams;
      if (filter_criteria && _.isString(filter_criteria)) {
        const error = new Error(`filter_criteria is a string not an object`);
        trackException(error, {
          extra: {
            flowId,
            nodeId,
            isTemplate,
            rawParams: params,
            cleanedParams,
          },
        });
      }

      return dispatch({
        type: ActionTypes.UPDATE_NODE_PARAMS,
        flowId,
        nodeId,
        params,
        fieldKey,
        meta,
        isDirtyNeeds,
        promise: Data.patchNode(
          nodeId,
          {
            params: cleanedParams,
            meta,
          },
          { isTemplate }
        ).then(node => {
          if (isDirtyNeeds) {
            dispatch(actions.loadNeeds(flowId, nodeId));
          }
          if (cb && typeof cb === 'function') {
            cb();
          }
          return node;
        }),
        resultKey: 'node',
      });
    };
  },

  draftUpdateNodeParams(flowId, nodeId, params, { fieldKey, meta } = {}) {
    return {
      type: ActionTypes.UPDATE_NODE_PARAMS,
      flowId,
      nodeId,
      params,
      fieldKey,
      meta,
    };
  },

  autosetupNode(nodeId) {
    return {
      type: ActionTypes.AUTOSETUP_NODE,
      nodeId,
      promise: Data.autosetupNode(nodeId),
    };
  },

  copy(zap) {
    return dispatch => {
      return dispatch({
        type: ActionTypes.COPY_FLOW,
        zap,
        promise: Data.copyZap(_.omit(zap, 'title')).then(data => {
          dispatch(
            TrackActions.trackUseCaseFound({
              referrer: 'copied-zap',
              nodeId: data.id,
            })
          );
          return data;
        }),
      });
    };
  },

  clearCopiedZaps() {
    return {
      type: ActionTypes.CLEAR_COPIED_ZAPS,
    };
  },

  move(flowId, folderId) {
    return {
      type: ActionTypes.MOVE_FLOW,
      flowId,
      folderId,
      promise: Data.moveZap(flowId, folderId),
    };
  },

  remove(flowId) {
    return {
      type: ActionTypes.REMOVE_FLOW,
      flowId,
      promise: Data.deleteZap(flowId),
    };
  },

  removeBulk(flowIds) {
    return (dispatch, getState) => {
      // We shouldn't delete unpaused zaps, so let's just ignore those.
      flowIds = flowIds.filter(flowId => {
        const flow = selectFlow(flowId, getState());
        return flow && flow.paused;
      });
      dispatch({
        type: ActionTypes.REMOVE_FLOWS,
        promise: Data.deleteZaps(flowIds),
        flowIds,
      });
    };
  },

  removeNode(flowId, nodeId, routeTo) {
    return (dispatch, getState) => {
      const state = getState();
      const node = selectNode(state, { flowId, nodeId });
      const shouldCollapse = !isPathsGroupNode(node) && !isPathNode(node);
      const isTemplate = selectIsFlowTemplate(state, { flowId, nodeId });

      const flow = selectFlow(flowId, state);
      const parentNodeId = getParentId(node);
      const shouldDeleteParent = isLastPathInGroup(flow, nodeId);

      if (routeTo) {
        dispatch(routeToAction(routeTo));
      }

      return dispatch({
        type: ActionTypes.REMOVE_NODE,
        flowId,
        nodeId,
        promise: Data.deleteNode(nodeId, { shouldCollapse, isTemplate })
          .then(() => {
            return Data.node(flowId, { isTemplate });
          })
          .then(rootNode => {
            // Update root node plan limits
            dispatch(
              actions.updateNodeValidation(
                flowId,
                rootNode.id,
                rootNode.plan_limits
              )
            );

            return {
              flowId,
              nodeId,
              paused: rootNode.paused,
            };
          }),
      }).then(result => {
        if (shouldDeleteParent) {
          dispatch(actions.removeNode(flowId, parentNodeId));
        }
        return result;
      });
    };
  },

  runNode(flowId, nodeId, taskState, params, onDangerouslyReceiveSuccess) {
    return (dispatch, getState) => {
      params = params || {};
      const isFlowTemplate = selectIsFlowTemplate(getState(), {
        flowId,
        nodeId,
      });
      const node = selectNode(getState(), { flowId, nodeId });
      const useAppSample = isFlowTemplate && !NodeUtils.requiresLiveData(node);

      const maybeAppSource = useAppSample && 'app';

      params.source = maybeAppSource || params.source || 'persisted';

      if (isFlowTemplate) {
        Object.assign(params, ZT_API_PARAM);

        // Always default fallback to app when using persisted source.
        if (!params.fallback && params.source === 'persisted') {
          params.fallback = 'app';
        }
      }

      return dispatch({
        type: ActionTypes.RUN_NODE,
        flowId,
        nodeId,
        promise: Data.runNode(nodeId, taskState, params),
        resultKey: 'taskStates',
        runSource: params.source,
        onDangerouslyReceiveSuccess,
      });
    };
  },

  refreshNode(
    flowId,
    nodeId,
    attrs,
    oldTaskState,
    onDangerouslyReceiveSuccess,
    services
  ) {
    return (dispatch, getState) => {
      const state = getState();
      const node = get(state.flow, ['flows', flowId, 'nodes', nodeId]);
      const shouldRunNode = NodeUtils.isConfigured(node, services);
      const isTemplate = selectIsFlowTemplate(state, { flowId, nodeId });
      const requiresLiveData = NodeUtils.requiresLiveData(node);
      const source = isTemplate && !requiresLiveData ? 'app' : null;

      return dispatch({
        type: ActionTypes.REFRESH_NODE,
        flowId,
        nodeId,
        attrs,
        promise: Data.refreshNode(flowId, nodeId, attrs, {
          oldTaskState,
          shouldRunNode,
          source,
          isTemplate,
        }),
        onDangerouslyReceiveSuccess,
      });
    };
  },

  loadAppSample(flowId, nodeId) {
    return {
      type: ActionTypes.LOAD_APP_SAMPLE,
      flowId,
      nodeId,
      promise: Data.runNode(
        nodeId,
        {},
        {
          source: 'app',
          fallback: 'disabled',
          is_template: true,
        }
      ),
    };
  },

  removeSample(flowId, nodeId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });
      return dispatch({
        type: ActionTypes.REMOVE_NODE_SAMPLE,
        flowId,
        nodeId,
        promise: Data.deleteSample(nodeId, { isTemplate }),
      });
    };
  },

  addSubscription(flowId, nodeId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });

      return dispatch({
        type: ActionTypes.ADD_NODE_SUBSCRIPTION,
        flowId,
        nodeId,
        promise: Data.createSubscription(nodeId, { isTemplate }),
        resultKey: 'node',
      });
    };
  },

  removeSubscription(flowId, nodeId) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });

      return dispatch({
        type: ActionTypes.REMOVE_NODE_SUBSCRIPTION,
        flowId,
        nodeId,
        promise: Data.deleteSubscription(nodeId, { isTemplate }),
        resultKey: 'node',
      });
    };
  },

  unpause(flowId, origin) {
    return (dispatch, getState) => {
      const flow = getState().flow.flows[flowId];
      if (isZapInTrash(flow)) {
        return dispatch({
          type: ActionTypes.UNPAUSE_FLOW_FAIL,
          flowId,
          error: new Error(
            'Zap cannot be turned on in the trash. Please move the Zap out of the trash first.'
          ),
        });
      }
      if (selectIsFlowTemplate(getState(), { flowId })) {
        return dispatch({
          type: ActionTypes.UNPAUSE_FLOW_FAIL,
          flowId,
          error: new Error('Zap Templates cannot be turned on.'),
        });
      }
      return dispatch({
        type: ActionTypes.UNPAUSE_FLOW,
        flowId,
        promise: Data.unpause(flowId, origin).then(() =>
          dispatch(Account.actions.reloadCurrentAccount())
        ),
      });
    };
  },

  unpauseAll(pausedZaps) {
    return dispatch => {
      return pausedZaps.reduce((promise, zap) => {
        return !zap || !zap.paused
          ? Promise.resolve()
          : promise.then(() => dispatch(actions.unpause(zap.id)));
      }, Promise.resolve());
    };
  },

  pause(flowId) {
    return dispatch => {
      dispatch({
        type: ActionTypes.PAUSE_FLOW,
        flowId,
        promise: Data.pause(flowId).then(() => {
          dispatch(Account.actions.reloadCurrentAccount());
        }),
      });
    };
  },

  clearErrorsScroll() {
    return {
      type: ActionTypes.CLEAR_FLOW_ERRORS_SCROLL,
    };
  },

  setAuthStatus(flowId, status, error) {
    return {
      type: ActionTypes.SET_FLOW_AUTH_STATUS,
      flowId,
      status,
      error,
    };
  },

  onWaitForSampleState(flowId) {
    return {
      type: ActionTypes.ON_WAIT_FOR_SAMPLE_STATE,
      flowId,
    };
  },

  onLoadSampleState(source, flowId) {
    return {
      type: ActionTypes.ON_LOAD_SAMPLE_STATE,
      flowId,
      source,
    };
  },

  onLoadSampleStateDone(flowId) {
    return {
      type: ActionTypes.ON_LOAD_SAMPLE_STATE_DONE,
      flowId,
    };
  },

  onRetrySampleState(flowId) {
    return {
      type: ActionTypes.ON_RETRY_SAMPLE_STATE,
      flowId,
    };
  },

  onRetrySampleStateDone(flowId) {
    return {
      type: ActionTypes.ON_RETRY_SAMPLE_STATE_DONE,
      flowId,
    };
  },

  onCompleteWaitForSample(flowId, isTimeout) {
    return {
      type: ActionTypes.ON_COMPLETE_WAIT_FOR_SAMPLE,
      flowId,
      isTimeout,
    };
  },

  routeToErroredStep(flow, node) {
    return (dispatch, getState) => {
      const needs = _.get(flow, ['needs', node.id], []);
      const hasNeeds = !_.isEmpty(needs);
      const flowId = flow.id;
      const nodeId = node.id;
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });

      if (hasNeeds) {
        dispatch(
          routeToAction(
            createEditorUrl({ flowId, nodeId, pageKey: 'fields', isTemplate })
          )
        );
      } else {
        const nextPage = pages.nextPage(
          _.pick(flow, ['nodeId', 'services', 'auths'])
        );
        if (nextPage) {
          dispatch(routeToAction(nextPage.href));
        } else {
          dispatch(routeBackTo(createEditorUrl({ flowId, isTemplate })));
        }
      }
    };
  },

  // Toggle open or closing a step menu in the sidebar,
  // keyed by step index, zero based
  toggleStepMenu(flowId, index, isOpened) {
    return {
      type: ActionTypes.TOGGLE_STEP_MENU,
      flowId,
      index,
      isOpened,
    };
  },

  // Force a sidebar step menu open, even if it's already open
  openStepMenu(flowId, index) {
    return {
      type: ActionTypes.OPEN_STEP_MENU,
      flowId,
      index,
    };
  },

  // Force a sidebar step menu closed, even if it's already closed
  closeStepMenu(flowId, index) {
    return {
      type: ActionTypes.CLOSE_STEP_MENU,
      flowId,
      index,
    };
  },

  // Just a silly temporary action to be called from legacy editor.
  markDirty(flowId) {
    return {
      type: ActionTypes.MARK_ZAP_DIRTY,
      flowId,
    };
  },

  sanityCheckFailed(flowId, sanityCheck) {
    return {
      type: ActionTypes.SANITY_CHECK_FAILED,
      flowId,
      sanityCheck,
      error: sanityCheck,
    };
  },

  cleanNodeParams(flowId, nodeId) {
    return {
      type: ActionTypes.CLEAN_NODE_PARAMS,
      flowId,
      nodeId,
    };
  },

  loadRecentIncidents() {
    return {
      type: ActionTypes.LOAD_RECENT_INCIDENTS,
      promise: Data.recentIncidents(),
      resultKey: 'incidents',
    };
  },

  editStepTitle(flowId, nodeId) {
    return {
      type: ActionTypes.EDIT_STEP_TITLE,
      flowId,
      nodeId,
    };
  },

  closeStepTitleEditor(flowId, nodeId, meta) {
    return (dispatch, getState) => {
      const isTemplate = selectIsFlowTemplate(getState(), { flowId, nodeId });

      return dispatch({
        type: ActionTypes.CLOSE_STEP_TITLE_EDITOR,
        flowId,
        nodeId,
        meta,
        promise: Data.patchNode(nodeId, { meta }, { isTemplate }),
      });
    };
  },

  updateNotes(flowId, note) {
    return {
      type: ActionTypes.UPDATE_FLOW_NOTES,
      flowId,
      note,
    };
  },

  saveNotes(flowId, note) {
    return {
      type: ActionTypes.SAVE_FLOW_NOTES,
      flowId,
      note,
      promise: Data.patchNode(flowId, { note }, {}),
    };
  },

  saveTimezone(flowId, timezone) {
    return (dispatch, getState) => {
      const flow = selectFlow(flowId, getState());
      const rootNode = getRootNode(flow.nodes);
      dispatch({
        type: ActionTypes.UPDATE_FLOW_TIMEZONE,
        flowId,
        timezone,
        promise: Data.patchNode(
          flowId,
          { meta: { ...rootNode.meta, timezone } },
          {}
        ),
      });
    };
  },

  setDefaultPreviewToggleState(flowId, nodeId, isPreviewing = true) {
    return {
      type: ActionTypes.SET_DEFAULT_PREVIEW_TOGGLE_STATE,
      flowId,
      nodeId,
      isPreviewing,
    };
  },

  onTogglePreviewState(flowId, nodeId) {
    return {
      type: ActionTypes.TOGGLE_PREVIEW_STATE,
      flowId,
      nodeId,
    };
  },

  exitFlow(flowId) {
    return {
      type: ActionTypes.EXIT_FLOW,
      flowId,
    };
  },

  exitFlows() {
    return {
      type: ActionTypes.EXIT_FLOWS,
    };
  },

  addPath(flowId, pathsGroupNodeId) {
    return (dispatch, getState) => {
      const flow = selectFlow(flowId, getState());
      const genPathValues = makePathNodeAttrsGenerator(flow);
      return dispatch(
        actions.createPath(flowId, pathsGroupNodeId, genPathValues())
      );
    };
  },

  getPublishForm(flowId) {
    return {
      type: ActionTypes.GET_PUBLISH_FORM,
      flowId,
      promise: Data.getPublishForm(flowId),
    };
  },

  updatePublishForm(flowId, values) {
    return (dispatch, getState) => {
      dispatch({
        type: ActionTypes.UPDATE_PUBLISH_FORM,
        flowId,
        promise: Data.updatePublishForm(flowId, values)
          .then(({ value }) => {
            const messageText =
              value.status === 'for-review'
                ? 'Your Zap template has been successfully submitted to the Zapier team for review.'
                : 'Your Zap template has been saved.';

            dispatch(
              actions.updatePublishMessages(flowId, {
                text: messageText,
                copyId: value.status === 'shared' ? flowId : null,
              })
            );

            return value;
          })
          .catch(error => {
            if (!error.json || !error.json.errors) throw error;

            // Maintain the same order as fields in the form.
            const publish = selectCurrentPublishForm(getState(), { flowId });
            const errors = publish.fields
              .map(field => field.key)
              .map(key => {
                const errMsgs = error.json.errors[key];
                const errMsg = [].concat(errMsgs).join(' ');
                return errMsg ? [key, errMsg] : null;
              })
              .filter(Boolean);

            dispatch(
              actions.updatePublishMeta(flowId, {
                errors,
                isWorking: false,
              })
            );
          }),
      });
    };
  },

  updatePublishMeta(flowId, payload) {
    return {
      type: ActionTypes.UPDATE_PUBLISH_META,
      flowId,
      payload,
    };
  },

  updatePublishMessages(flowId, { text, copyId }) {
    return {
      type: ActionTypes.UPDATE_PUBLISH_MESSAGES,
      flowId,
      payload: { text, copyId },
    };
  },

  createPathsGroup(flowId, parentNodeId) {
    return (dispatch, getState) => {
      dispatch(actions.addStep(flowId));
      const state = getState();

      const accountId = Account.selectors.all.currentAccountId(state);
      const flow = selectFlow(flowId, state);

      const genPathAttrs = makePathNodeAttrsGenerator(flow);
      const firstPathAttrs = genPathAttrs();
      const secondPathAttrs = genPathAttrs();

      return dispatch({
        type: ActionTypes.CREATE_PATHS_GROUP,
        flowId,
        accountId,
        promise: Data.createPathsGroup(accountId, flowId, parentNodeId, [
          firstPathAttrs,
          secondPathAttrs,
        ]),
      }).then(({ result }) => {
        const { pathsGroupNode, rootNode } = result;

        // Update root node validation
        dispatch(
          actions.updateNodeValidation(
            flowId,
            rootNode.id,
            rootNode.plan_limits
          )
        );
        dispatch(actions.addStepDone(flowId));

        return dispatch(
          routeToAction(
            createEditorUrl({
              flowId,
              nodeId: pathsGroupNode.id,
              pageKey: PATH_SETTINGS_PAGE,
            })
          )
        );
      });
    };
  },

  showEditorIssues() {
    return {
      type: ActionTypes.SHOW_EDITOR_ISSUES,
    };
  },

  dismissError(dismissedError) {
    return {
      type: ActionTypes.DISMISS_EDITOR_ERROR,
      dismissedError,
    };
  },

  showEditorSettings() {
    return {
      type: ActionTypes.SHOW_EDITOR_SETTINGS,
    };
  },

  hideEditorSettings() {
    return {
      type: ActionTypes.HIDE_EDITOR_SETTINGS,
    };
  },

  onOpenAddStepPicker(flowId, nodeId) {
    return { type: ActionTypes.ON_OPEN_ADD_STEP_PICKER, flowId, nodeId };
  },

  onCloseAddStepPicker(flowId) {
    return { type: ActionTypes.ON_CLOSE_ADD_STEP_PICKER, flowId };
  },

  onTrackChangeApp(service) {
    return dispatch => {
      if (ServiceUtils.isFeatured(service)) {
        dispatch(
          /**
           * @trackingEvent Editor Step
           * @trackingCompletionEvent none
           * @trackingParam account_id - current user account id
           * @trackingParam path - current relative editor url
           * @trackingParam selected_api - app key
           * @trackingParam userId - current user id
           * @trackingParam which - 'Popular' or 'Utility' depending on the selected_api
           * @trackingDescription User picked different app
           */
          TrackActions.track('Editor Step', {
            which: 'Popular',
            selected_api: service.key,
          })
        );
      }
      if (ServiceUtils.isBuiltIn(service)) {
        dispatch(
          TrackActions.track('Editor Step', {
            which: 'Utility',
            selected_api: service.key,
          })
        );
      }
    };
  },

  onChangeApp({
    flowId,
    node,
    service,
    onDangerouslyReceiveSuccess,
    shouldUseSimpleZapSetup,
  }) {
    return dispatch => {
      dispatch(actions.onTrackChangeApp(service));

      const action = getDefaultAction(service.key);

      const currentAttrs = node;
      const desiredAttrs = { selected_api: service.key };
      const isAddingPaths = isPathApi(service.key);
      const pathAttrs = isAddingPaths ? PATHS_GROUP_ATTRS : {};
      const meta = getRetainedNodeMeta(node);
      const overrideAttrs = {
        action,
        type_of: getCorrectNodeTypeOf(service.key, node),
        authentication_id: null,
        params: {},
        meta,
        ...pathAttrs,
      };

      const cleanedAttrs = NodeUtils.mergeNodeAttrs(
        currentAttrs,
        desiredAttrs,
        overrideAttrs
      );

      dispatch({
        type: ActionTypes.CHANGE_APP,
        nodeId: node.id,
        selected_api: service.key,
      });

      // Update the node before possibly routing to next step. This avoids a race
      // condition where the next step could end up briefly loading
      // needs for the previously selected app.
      dispatch(
        actions.saveUpdateNode(flowId, node.id, cleanedAttrs, {
          shouldUseSimpleZapSetup,
          onDangerouslyReceiveSuccess,
        })
      ).then(result => {
        // Update plan limits if needed
        const planLimits = _.get(result, ['node', 'plan_limits'], null);
        dispatch(actions.updateNodeValidation(flowId, node.id, planLimits));

        if (isFilterAPI(service.key)) {
          // Filters have no explicit action, so if it's a filter route to the 'filter' sub-step:
          const routeTo = createEditorUrl({
            flowId,
            nodeId: node.id,
            pageKey: 'filter',
          });
          dispatch(routeToAction(routeTo));
        }
      });
    };
  },

  changeAction: ({ flowId, node, action, type_of, isAutoSelection }) => {
    return {
      type: ActionTypes.CHANGE_ACTION,
      flowId,
      node,
      action,
      type_of,
      isAutoSelection,
    };
  },

  loadPublicZap: guid => dispatch => {
    return dispatch({
      type: ActionTypes.LOAD_PUBLIC_ZAP,
      promise: Data.publicZap(guid).then(publicZap => {
        const sharedZapId = selectPublicZapRecipeId(publicZap);
        const url = `/app/editor/template/${sharedZapId}/welcome`;
        dispatch(routeToAction(url));
      }),
    });
  },

  gotoNextSimplifiedSubStep({ flow, nodeId }) {
    return dispatch => {
      const continueActions = getContinueFromStepActions({
        flow,
        nodeId,
      });
      continueActions.forEach(dispatch);
    };
  },

  sendSimplifiedEditorFeedback(feedback) {
    return (dispatch, getState) => {
      const state = getState();
      const currentProfileId = Profile.selectors.all.currentProfileId(state);
      const { hostname } = location;
      const timestamp = new Date().toUTCString();

      const payload = { timestamp, currentProfileId, hostname, ...feedback };

      fetch('https://hooks.zapier.com/hooks/catch/1687901/oyf80ec/', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
        .then(res => res.json())
        .then(json =>
          console.info(`Simplified editor feedback submitted`, payload, json)
        )
        .catch(err =>
          console.error(`Failed to submit simplified editor feedback`, err)
        );
    };
  },

  clearNodeValidations({ flowId, nodeId }) {
    return { type: ActionTypes.CLEAR_NODE_VALIDATION, flowId, nodeId };
  },

  clearNodeValidationsOnTrialExtension({ flowId, nodeId }) {
    return (dispatch, getState) => {
      const state = getState();
      const currentAccount = Account.selectors.all.currentAccount(state);
      const didExtendTrial = Account.selectors.didExtendTrial(currentAccount);
      if (didExtendTrial) {
        dispatch(actions.clearNodeValidations({ flowId, nodeId }));
      }
    };
  },
};

export default actions;
